import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { User, Plus, Trash2, Check, X, ChevronDown, ChevronRight, Mail, Phone } from 'lucide-react';
import { BeneficialOwner } from '../types';
import { cn } from '@/lib/utils';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface BeneficialOwnersDisplayProps {
  beneficialOwners: BeneficialOwner[];
  onOwnerChange: (index: number, field: string, value: string) => void;
  onSave: () => Promise<void>;
  onAddOwner: () => void;
  onRemoveOwner: (index: number) => void;
}

export function BeneficialOwnersDisplay({
  beneficialOwners,
  onOwnerChange,
  onSave,
  onAddOwner,
  onRemoveOwner,
}: BeneficialOwnersDisplayProps) {
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const totalOwnership = beneficialOwners.reduce(
    (sum, owner) => sum + (Number(owner.ownershipPercentage) || 0),
    0
  );

  const getOwnershipColor = (percentage: number) => {
    if (percentage === 100) return 'bg-green-100 text-green-800 border-green-300';
    if (percentage > 100) return 'bg-red-100 text-red-800 border-red-300';
    return 'bg-yellow-100 text-yellow-800 border-yellow-300';
  };

  const formatSSN = (value: string): string => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return numbers;
    if (numbers.length <= 5) return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 5)}-${numbers.slice(5, 9)}`;
  };

  const formatPhone = (value: string): string => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 3) return `(${numbers}`;
    if (numbers.length <= 6) return `(${numbers.slice(0, 3)}) ${numbers.slice(3)}`;
    return `(${numbers.slice(0, 3)}) ${numbers.slice(3, 6)}-${numbers.slice(6, 10)}`;
  };

  const allowedTitles = [
    'CEO',
    'Director',
    'Owner',
    'Partner',
    'President',
    'Secretary',
    'Treasurer',
    'Vice President',
  ];

  const toMmDdYy = (isoOrText: string): string => {
    if (!isoOrText) return '';
    const isoMatch = isoOrText.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (isoMatch) {
      const [, y, m, d] = isoMatch;
      return `${m}/${d}/${y.slice(2)}`;
    }
    const mdMatch = isoOrText.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{2,4})$/);
    if (mdMatch) {
      const [, rawMonth, rawDay, rawYear] = mdMatch as unknown as [string, string, string, string];
      const year = rawYear.length === 4 ? rawYear.slice(2) : rawYear;
      return `${rawMonth.padStart(2, '0')}/${rawDay.padStart(2, '0')}/${year}`;
    }
    return isoOrText;
  };

  const handleSave = async (index: number) => {
    await onSave();
    setEditingIndex(null);
  };

  const handleAddNew = () => {
    onAddOwner();
    setIsAddingNew(true);
    setEditingIndex(beneficialOwners.length);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center gap-2">
          <Badge
            variant="outline"
            className={cn('px-3 py-1', getOwnershipColor(totalOwnership))}
          >
            Total Ownership: {totalOwnership}%
          </Badge>
        </div>
        <Button size="sm" onClick={handleAddNew}>
          <Plus className="h-4 w-4 mr-2" />
          Add Owner
        </Button>
      </div>

      {beneficialOwners.map((owner, index) => (
        <Card key={index} className="relative">
          <CardContent className="pt-6">
            {editingIndex === index ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>First Name</Label>
                    <Input
                      value={owner.firstName}
                      onChange={(e) => onOwnerChange(index, 'firstName', e.target.value)}
                      placeholder="First Name"
                      className={cn(
                        "focus-visible:ring-2 focus-visible:ring-primary/60",
                        !owner.firstName && "ring-1 ring-gray-200 bg-gray-50/50"
                      )}
                    />
                  </div>
                  <div>
                    <Label>Last Name</Label>
                    <Input
                      value={owner.lastName}
                      onChange={(e) => onOwnerChange(index, 'lastName', e.target.value)}
                      placeholder="Last Name"
                      className={cn(
                        "focus-visible:ring-2 focus-visible:ring-primary/60",
                        !owner.lastName && "ring-1 ring-gray-200 bg-gray-50/50"
                      )}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Title</Label>
                    <Select
                      value={owner.title || ''}
                      onValueChange={(value) => onOwnerChange(index, 'title', value)}
                    >
                      <SelectTrigger className={cn(
                        "h-9 focus-visible:ring-2 focus-visible:ring-primary/60",
                        !owner.title && "ring-1 ring-gray-200 bg-gray-50/50"
                      )}>
                        <SelectValue placeholder="Select title" />
                      </SelectTrigger>
                      <SelectContent>
                        {allowedTitles.map((t) => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Ownership %</Label>
                    <Input
                      value={owner.ownershipPercentage}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow empty value
                        if (value === '') {
                          onOwnerChange(index, 'ownershipPercentage', value);
                          return;
                        }
                        // Parse and clamp to 0-100
                        const numValue = parseFloat(value);
                        if (!isNaN(numValue)) {
                          const clampedValue = Math.min(100, Math.max(0, numValue));
                          onOwnerChange(index, 'ownershipPercentage', clampedValue.toString());
                        }
                      }}
                      placeholder="25"
                      type="number"
                      min="0"
                      max="100"
                      className={cn(
                        "focus-visible:ring-2 focus-visible:ring-primary/60",
                        !owner.ownershipPercentage && "ring-1 ring-gray-200 bg-gray-50/50"
                      )}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Date of Birth</Label>
                    <Input
                      value={owner.dateOfBirth}
                      onChange={(e) => onOwnerChange(index, 'dateOfBirth', e.target.value)}
                      placeholder="MM/DD/YY"
                      className={cn(
                        "focus-visible:ring-2 focus-visible:ring-primary/60",
                        !owner.dateOfBirth && "ring-1 ring-gray-200 bg-gray-50/50"
                      )}
                    />
                  </div>
                  <div>
                    <Label>SSN</Label>
                    <Input
                      value={owner.ssn}
                      onChange={(e) => onOwnerChange(index, 'ssn', formatSSN(e.target.value))}
                      placeholder="123-45-6789"
                      maxLength={11}
                      className={cn(
                        "focus-visible:ring-2 focus-visible:ring-primary/60",
                        !owner.ssn && "ring-1 ring-gray-200 bg-gray-50/50"
                      )}
                    />
                  </div>
                  <div />
                </div>

                <div>
                  <Label>Driver's License</Label>
                  <Input
                    value={owner.driverLicense}
                    onChange={(e) => onOwnerChange(index, 'driverLicense', e.target.value)}
                    placeholder="DL123456789"
                    className={cn(
                      "focus-visible:ring-2 focus-visible:ring-primary/60",
                      !owner.driverLicense && "ring-1 ring-gray-200 bg-gray-50/50"
                    )}
                  />
                </div>

                <div>
                  <Label>Address</Label>
                  <Input
                    value={owner.address}
                    onChange={(e) => onOwnerChange(index, 'address', e.target.value)}
                    placeholder="Street Address"
                    className={cn(
                      "focus-visible:ring-2 focus-visible:ring-primary/60",
                      !owner.address && "ring-1 ring-gray-200 bg-gray-50/50"
                    )}
                  />
                </div>

                <div className="grid grid-cols-6 gap-2">
                  <div className="col-span-3">
                    <Label>City</Label>
                    <Input
                      value={owner.city}
                      onChange={(e) => onOwnerChange(index, 'city', e.target.value)}
                      placeholder="City"
                      className={cn(
                        "focus-visible:ring-2 focus-visible:ring-primary/60",
                        !owner.city && "ring-1 ring-gray-200 bg-gray-50/50"
                      )}
                    />
                  </div>
                  <div className="col-span-1">
                    <Label>State</Label>
                    <Input
                      value={owner.state}
                      onChange={(e) => {
                        let value = e.target.value;
                        if (value.startsWith('US-')) {
                          value = value.replace('US-', '');
                        }
                        onOwnerChange(index, 'state', value);
                      }}
                      placeholder="ST"
                      maxLength={2}
                      className={cn(
                        "focus-visible:ring-2 focus-visible:ring-primary/60",
                        !owner.state && "ring-1 ring-gray-200 bg-gray-50/50"
                      )}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>ZIP</Label>
                    <Input
                      value={owner.zipCode}
                      onChange={(e) => onOwnerChange(index, 'zipCode', e.target.value)}
                      placeholder="12345"
                      className={cn(
                        "focus-visible:ring-2 focus-visible:ring-primary/60",
                        !owner.zipCode && "ring-1 ring-gray-200 bg-gray-50/50"
                      )}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={owner.phone}
                      onChange={(e) => onOwnerChange(index, 'phone', formatPhone(e.target.value))}
                      placeholder="(555) 123-4567"
                      type="tel"
                      className={cn(
                        "focus-visible:ring-2 focus-visible:ring-primary/60",
                        !owner.phone && "ring-1 ring-gray-200 bg-gray-50/50"
                      )}
                    />
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      value={owner.email}
                      onChange={(e) => onOwnerChange(index, 'email', e.target.value)}
                      placeholder="email@example.com"
                      type="email"
                      className={cn(
                        "focus-visible:ring-2 focus-visible:ring-primary/60",
                        !owner.email && "ring-1 ring-gray-200 bg-gray-50/50"
                      )}
                    />
                  </div>
                </div>

                <div className="flex gap-2 justify-end">
                  <Button size="sm" onClick={() => handleSave(index)}>
                    <Check className="h-4 w-4 mr-1" />
                    Save
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      if (isAddingNew && index === beneficialOwners.length - 1) {
                        onRemoveOwner(index);
                      }
                      setEditingIndex(null);
                      setIsAddingNew(false);
                    }}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => {
                      onRemoveOwner(index);
                      setEditingIndex(null);
                      setIsAddingNew(false);
                    }}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            ) : (
              <div>
                {/* Collapsed view - always visible */}
                <div 
                  className="cursor-pointer hover:bg-gray-50 rounded p-2 -m-2"
                  onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                >
                  <div className="space-y-1">
                    {/* Line 1: Name (left), Phone (right) - no icons */}
                    <div className="grid grid-cols-12 items-center gap-3">
                      <div className="col-span-7 flex items-center gap-2 min-w-0">
                        {expandedIndex === index ? (
                          <ChevronDown className="h-4 w-4 text-gray-400" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-gray-400" />
                        )}
                        <span className="font-medium truncate">
                          {owner.firstName} {owner.lastName}
                        </span>
                      </div>
                      <div className="col-span-5 text-right text-sm text-gray-700 truncate">
                        {owner.phone || 'No phone'}
                      </div>
                    </div>

                    {/* Line 2: Email (clickable), Title badge, Ownership % */}
                    <div className="grid grid-cols-12 items-center gap-3 text-sm">
                      <div className="col-span-5 min-w-0 overflow-hidden">
                        {owner.email ? (
                          <a
                            href={`mailto:${owner.email}`}
                            className="text-blue-600 hover:underline block truncate"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {owner.email}
                          </a>
                        ) : (
                          <span className="text-gray-500">No email</span>
                        )}
                      </div>
                      <div className="col-span-7 flex items-center justify-end gap-2">
                        <Badge variant="outline" className="text-[10px] px-2 py-0.5 whitespace-nowrap">
                          {owner.title || 'Owner'}
                        </Badge>
                        <Badge variant="outline" className="text-xs px-2 py-0.5">
                          {owner.ownershipPercentage || 0}%
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Expanded view - only when expanded */}
                {expandedIndex === index && (
                  <div
                    className="mt-3 pt-3 border-t border-gray-100 cursor-pointer hover:bg-gray-50 rounded-md p-2 -m-2 transition-colors"
                    onClick={() => setEditingIndex(index)}
                  >
                    {/* Compact summary to preview key fields */}
                    <div className="grid grid-cols-12 gap-3 mb-3 items-center">
                      <div className="col-span-6 text-sm">
                        <span className="text-gray-500">Title:</span>{' '}
                        <span className="font-medium">{owner.title || 'Not provided'}</span>
                      </div>
                      <div className="col-span-6 text-sm">
                        <span className="text-gray-500">Ownership %:</span>{' '}
                        <span className="font-medium">{owner.ownershipPercentage || '0'}</span>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Title:</span>{' '}
                        <span className="font-medium">{owner.title || 'Not provided'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">SSN:</span>{' '}
                        <span className="font-medium">
                          {owner.ssn || 'Not provided'}
                        </span>
                      </div>
                      <div>
                        <span className="text-gray-500">DOB:</span>{' '}
                        <span className="font-medium">{toMmDdYy(owner.dateOfBirth || '') || 'Not provided'}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Driver's License:</span>{' '}
                        <span className="font-medium">{owner.driverLicense || 'Not provided'}</span>
                      </div>
                      <div className="col-span-2">
                        <span className="text-gray-500">Address:</span>{' '}
                        <span className="font-medium">
                          {owner.address && owner.city && owner.state && owner.zipCode
                            ? `${owner.address}, ${owner.city}, ${owner.state} ${owner.zipCode}`
                            : 'Not provided'}
                        </span>
                      </div>
                      <div className="col-span-2">
                        <span className="text-gray-500">Email:</span>{' '}
                        {owner.email ? (
                          <a
                            href={`mailto:${owner.email}`}
                            className="text-blue-600 hover:underline font-medium"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {owner.email}
                          </a>
                        ) : (
                          <span className="font-medium">Not provided</span>
                        )}
                      </div>
                      <div className="col-span-2">
                        <span className="text-gray-500">Phone:</span>{' '}
                        <span className="font-medium">{owner.phone || 'Not provided'}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      ))}

      {beneficialOwners.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <User className="h-12 w-12 mx-auto mb-2 text-gray-300" />
          <p>No beneficial owners added yet.</p>
          <Button className="mt-4" size="sm" onClick={handleAddNew}>
            <Plus className="h-4 w-4 mr-2" />
            Add First Owner
          </Button>
        </div>
      )}
    </div>
  );
}
